import _ from 'lodash';
import Vue from 'vue';
import VueRouter from 'vue-router';
import GuestParentView from '@/views/Guest/GuestParentView.vue';
import SignInView from '@/views/Guest/SignInView/SignInView.vue';
import PasswordRecoveryView from '@/views/Guest/PasswordRecoveryView/PasswordRecoveryView.vue';
import {
  AuthorizedParentView,
  BillingView,
  ChangeMyProvider,
  ChangeProSuccess,
  ChangeProFail,
  CrewNoShowView,
  InboxView,
  InstantQuoteDetailsView,
  LeadsInboxView,
  LearnMore,
  MessageLSView,
  OneTimeRescheduleView,
  OrderPricedServicesFormView,
  OrderServicesFormView,
  OrderServicesView,
  PauseSubscriptionView,
  BlockCancelSubscriptionView,
  ProfileView,
  ProfileUpdatePaymentView,
  PropertiesOptionalInfoView,
  PropertiesRequiredInfoView,
  PropertyDamageView,
  PropertyManagerView,
  QuoteDetailsView,
  ReactivatePoliciesView,
  ReactivateView,
  RecommendedServiceView,
  RequiredServiceView,
  ReturnRequestView,
  SelectCancelServiceView,
  ServiceDetailsView,
  ServiceHistoryView,
  ServicesView,
  ShareView,
  SkipMowView,
  SpecialInstructionsView,
  SupportView,
  WhatDayView,
  WhatOtherServicesView,
  WhatsIncludedView,
  WhatTimeView,
  LongGrassPricingView,
  LongGrassFeeReportView,
  LongGrassQuoteReportView,
  LongGrassScheduledServiceView,
  LongGrassDisputeView,
  LongGrassDisputeSuccess,
  LongGrassDisputeFail,
  PolicyView,
  LongGrassRejectedServiceView,
} from '@/views/Authorized';
import {
  OnboardingParentView,
  OnboardingBushCountView,
  OnboardingDistributeEarlyView,
  OnboardingPoliciesView,
  OnboardingPricedServicesView,
  OnboardingPropertyInfoView,
  OnboardingServicesView,
  OnboardingWelcomeShareView,
} from '@/views/Onboarding';
import {
  OnboardingLeadsParentView,
  OnboardingLeadsChooseProsView,
  OnboardingLeadsDetailsView,
  OnboardingLeadsShareContactDetailsView,
  OnboardingLeadsSuccessView,
} from '@/views/OnboardingLeads';

import store from '@/store';
import {
  AUTH__UPDATE_TOKEN,
  AUTH__LOGIN,
  AUTH__TOKEN,
  AUTH__IS_LOGGED_IN,
  AUTH__PROCESSING_ORDER,
  ONBOARDING__STEPS,
  FEATURE_FLAGS__ALL_FLAGS,
  ONBOARDING__POST_ORDER_NEXT_SCREEN,
  AUTH__IS_CUSTOMER_LEAD,
  AUTH__GET_TOKEN_FROM_ACCESS_CODE,
  PROPERTY_MANAGER__CURRENT_PROPERTY_ID,
  AUTH__IS_PROPERTY_MANAGER,
  PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID,
  CONVERSATION__GET_CONVERSATIONS,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
} from '@/store/types';
import { lsTrackingService } from 'ls-vue-common';
import dayjs from 'dayjs';
import { serviceType } from '@/helpers';
import GateLeftOpenView from '@/views/Authorized/SupportView/GateLeftOpenView/GateLeftOpenView.vue';

Vue.use(VueRouter);

const _private = {
  access: '',
  savedRouteTo: null,
};

const brand = process.env.VUE_APP_BRAND_THEME;

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '',
      name: 'guest',
      component: GuestParentView,
      children: [
        {
          path: '/login',
          name: 'login',
          component: SignInView,
        },
        {
          path: '/password-recovery',
          name: 'passwordRecovery',
          component: PasswordRecoveryView,
        },
      ],
    },
    {
      path: '',
      name: 'onboardingLeads',
      component: OnboardingLeadsParentView,
      meta: { requiresAuth: true, isOnboarding: true, allowForLeadsOnly: true },
      children: [
        {
          path: '/onboarding-leads/choose-pros',
          name: 'onboardingLeadsChooseProsView',
          component: OnboardingLeadsChooseProsView,
        },
        {
          path: '/onboarding-leads/success',
          name: 'onboardingleadsSuccessView',
          component: OnboardingLeadsSuccessView,
        },
        {
          path: '/onboarding-leads/details',
          name: 'onboardingleadsDetailsView',
          component: OnboardingLeadsDetailsView,
        },
        {
          path: '/onboarding-leads/share-contact-details',
          name: 'OnboardingLeadsShareContactDetailsView',
          component: OnboardingLeadsShareContactDetailsView,
        },
      ],
    },
    {
      path: '',
      name: 'onboarding',
      component: OnboardingParentView,
      meta: { requiresAuth: true, isOnboarding: true },
      children: [
        {
          path: '/onboarding/priced-services/:str_id',
          name: 'onboardingPricedServices',
          component: OnboardingPricedServicesView,
        },
        {
          path: '/onboarding/services/:service_id',
          name: 'onboardingServices',
          component: OnboardingServicesView,
        },
        {
          path: '/onboarding/property-info',
          name: 'onboardingPropertyInfo',
          component: OnboardingPropertyInfoView,
        },
        {
          path: '/onboarding/policies',
          name: 'onboardingPolicies',
          component: OnboardingPoliciesView,
          beforeEnter: onboardingPoliciesHandler,
        },
        {
          path: '/onboarding/early',
          name: 'onboardingDistributeEarly',
          component: OnboardingDistributeEarlyView,
          beforeEnter: standAloneTreatmentHandler,
        },
        {
          path: '/onboarding/bush-count',
          name: 'onboardingBushCountView',
          component: OnboardingBushCountView,
          beforeEnter: standAloneTreatmentHandler,
        },
        {
          path: '/onboarding/welcome',
          name: 'onboardingWelcomeShare',
          component: OnboardingWelcomeShareView,
        },
      ],
    },
    {
      path: '',
      name: 'authorized',
      component: AuthorizedParentView,
      meta: { requiresAuth: true },
      children: [
        // legacy redirects
        { path: '/profile/billing', redirect: { name: 'billing' } },
        { path: '/profile/settings', redirect: { name: 'profile' } },
        {
          path: '/profile/update-payment',
          name: 'profileUpdatePayment',
          component: ProfileUpdatePaymentView,
        },
        {
          path: '/billing',
          name: 'billing',
          component: BillingView,
        },
        {
          path: '/inbox',
          name: 'inbox',
          component: InboxView,
        },
        {
          path: '/inbox/:conversation_id',
          name: 'inboxConversation',
          component: InboxView,
        },
        {
          // property_id param made optional with ?
          path: '/services/property/:property_id?/request',
          name: 'orderService',
          component: OrderServicesView,
        },
        {
          path: '/services/property/:property_id/request/:service_id',
          name: 'orderServiceForm',
          component: OrderServicesFormView,
        },
        {
          path: '/services/property/:property_id/order/:str_id',
          name: 'orderPricedServiceForm',
          component: OrderPricedServicesFormView,
        },
        {
          path: '/services/property/:property_id/reactivate',
          name: 'reactivate',
          component: ReactivateView,
          meta: { allowForLeadsOnly: true },
        },
        {
          path: '/services/property/:property_id/reactivate/policies',
          name: 'reactivatePolicies',
          component: ReactivatePoliciesView,
          beforeEnter: preventNonProgrammaticRouting,
        },
        {
          path: '/services/property/:property_id/requiredservice/:requiredService_id',
          name: 'requiredService',
          component: RequiredServiceView,
        },
        {
          path: '/services/property/:property_id/recommendation/:id',
          name: 'recommendedService',
          component: RecommendedServiceView,
        },
        {
          path: '/profile',
          name: 'profile',
          component: ProfileView,
          meta: { allowForLeadsOnly: true },
        },
        {
          path: '/support/special-instructions-for-my-provider',
          name: 'specialInstructions',
          component: SpecialInstructionsView,
        },
        {
          path: '/support/skip-mow',
          name: 'skipMow',
          component: SkipMowView,
        },
        {
          path: '/support/pause-subscription',
          name: 'pauseSubscription',
          component: PauseSubscriptionView,
        },
        {
          path: '/support/cannot-cancel-subscription',
          name: 'blockCancelSubscription',
          component: BlockCancelSubscriptionView,
        },
        {
          path: '/services',
          name: 'services',
          component: ServicesView,
          meta: { allowForLeadsOnly: true },
        },
        {
          path: '/properties/:page?',
          name: 'properties',
          component: PropertyManagerView,
        },
        {
          path: 'long-grass/policy',
          name: 'LongGrassPolicy',
          component: PolicyView,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass-fee/:report_id',
          name: 'LongGrassFeeReport',
          component: LongGrassFeeReportView,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass-quote/:report_id',
          name: 'LongGrassQuoteReport',
          component: LongGrassQuoteReportView,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass-quote/:report_id/scheduled',
          name: 'LongGrassScheduledServiceView',
          component: LongGrassScheduledServiceView,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass-quote/:report_id/rejected',
          name: 'LongGrassRejectedServiceView',
          component: LongGrassRejectedServiceView,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass/:report_id/dispute',
          name: 'LongGrassDispute',
          component: LongGrassDisputeView,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass/:report_id/dispute/success',
          name: 'LongGrassDisputeSuccess',
          component: LongGrassDisputeSuccess,
        },
        {
          path:
            '/services/property/:property_id/subscription/:schedule_id/schedule-event/:scheduleevent_id/long-grass/:report_id/dispute/fail',
          name: 'LongGrassDisputeFail',
          component: LongGrassDisputeFail,
        },
        {
          path: '/services/property/:property_id/subscription/:schedule_id/long-grass/pricing',
          name: 'longGrassPricing',
          component: LongGrassPricingView,
        },
        {
          path: '/services/property/:property_id/subscription/:schedule_id',
          name: 'serviceDetails',
          component: ServiceDetailsView,
        },
        {
          path: '/services/property/:property_id/subscription/:schedule_id/history',
          name: 'serviceHistory',
          component: ServiceHistoryView,
        },
        {
          path: '/services/property/:property_id/quote/:schedule_id',
          name: 'quoteDetails',
          component: QuoteDetailsView,
        },
        {
          path: '/services/property/:property_id/instant-quote/:schedule_id',
          name: 'instantQuoteDetails',
          component: InstantQuoteDetailsView,
        },
        {
          path: '/support/change-my-provider',
          name: 'changeMyProvider',
          component: ChangeMyProvider,
        },
        {
          path: '/support/change-my-provider/learn-more',
          name: 'LearnMore',
          component: LearnMore,
        },
        {
          path: '/support/change-my-provider/success',
          name: 'ChangeProSuccess',
          component: ChangeProSuccess,
        },
        {
          path: '/support/change-my-provider/fail',
          name: 'ChangeProFail',
          component: ChangeProFail,
        },
        {
          path: '/support/my-crew-never-showed-up',
          name: 'crewNoShow',
          component: CrewNoShowView,
        },
        {
          path: `/support/message-${brand}`,
          name: 'messageLS',
          component: MessageLSView,
        },
        {
          path: '/support/my-property-was-damaged',
          name: 'propertyDamage',
          component: PropertyDamageView,
        },
        {
          path: '/support/one-time-reschedule',
          name: 'oneTimeReschedule',
          component: OneTimeRescheduleView,
        },
        {
          path: '/support/return-request',
          name: 'returnRequest',
          component: ReturnRequestView,
          beforeEnter: preventNonProgrammaticRouting,
        },
        {
          path: '/support/gate-left-open',
          name: 'gateLeftOpen',
          component: GateLeftOpenView,
          beforeEnter: preventNonProgrammaticRouting,
        },
        {
          path: '/support/whats-included-in-mowing-service',
          name: 'whatsIncluded',
          component: WhatsIncludedView,
        },
        {
          path: '/support/what-other-services-are-available',
          name: 'whatOtherServices',
          component: WhatOtherServicesView,
        },
        {
          path: '/support/what-time-will-crew-arrive',
          name: 'whatTime',
          component: WhatTimeView,
        },
        {
          path: '/support/lawn-mowing-scheduled-for-specific-day',
          name: 'whatDay',
          component: WhatDayView,
        },
        {
          path: '/support/cancel-service',
          name: 'selectCancelService',
          component: SelectCancelServiceView,
        },
        {
          path: '/share',
          name: 'share',
          component: ShareView,
        },
        {
          path: '/support',
          name: 'support',
          component: SupportView,
        },
        {
          path: '/properties/:property_id/customer-leads/:customer_lead_id/contact/:contact_id',
          name: 'leadsInbox',
          component: LeadsInboxView,
        },
        {
          path: '/properties/:property_id/required-info',
          name: 'propertiesRequiredInfo',
          component: PropertiesRequiredInfoView,
        },
        {
          path: '/properties/:property_id/optional-info',
          name: 'propertiesOptionalInfo',
          component: PropertiesOptionalInfoView,
        },
      ],
    },
  ],
});

export function isStandAloneTreatment() {
  const { type } = store.getters[AUTH__PROCESSING_ORDER];
  return type === serviceType.TREATMENT;
}

export function standAloneTreatmentHandler(to, _, next) {
  const skippableSteps = ['onboardingDistributeEarly', 'onboardingBushCountView'];
  const shouldSkip = isStandAloneTreatment() && skippableSteps.includes(to.name);

  if (shouldSkip) {
    return router.replace({ name: 'onboardingWelcomeShare' });
  }

  next();
}

export function onboardingPoliciesHandler(_to, _from, next) {
  const hasAcceptedPolicies = Boolean(
    _.get(store.getters[AUTH__PROCESSING_ORDER], 'policies_accepted_at')
  );

  if (hasAcceptedPolicies) {
    return router.replace({ name: 'onboardingDistributeEarly' });
  }

  next();
}

export function preventNonProgrammaticRouting(to, from, next) {
  if (!from.name) {
    router.replace({ name: 'services' });
  } else {
    next();
  }
}

export async function onSuccess(to, from, next) {
  const isOnboardingRoute = to.matched.some((record) => record.meta.isOnboarding);
  const allowForLeadsOnly = to.matched.some((record) => record.meta.allowForLeadsOnly);
  const isCustomerLead = store.getters[AUTH__IS_CUSTOMER_LEAD];
  const isPropertyManager = store.getters[AUTH__IS_PROPERTY_MANAGER];
  const currentPropertyId = store.getters[PROPERTY_MANAGER__CURRENT_PROPERTY_ID];
  const propertyIdFromParams = _.toNumber(_.get(to.params, 'property_id'));
  const conversationIdFromParams = _.toNumber(_.get(to.params, 'conversation_id'));

  // if this list grows we can put it in a constant file
  const customerLeadRoutes = {
    leadsInbox: true,
  };

  if (isCustomerLead && customerLeadRoutes[to.name]) {
    next();
  }
  // IF any user is trying to force-access an onboarding (or leads-onboarding) route
  // OR IF route is not explicitly for leads only but the user is leads only
  // THEN re-route to services
  else if (isOnboardingRoute || (!allowForLeadsOnly && isCustomerLead)) {
    next({
      name: 'services',
    });
  }
  // this covers the scenario where customer is a PM and refreshes the inbox view
  else if (isPropertyManager && conversationIdFromParams && currentPropertyId) {
    await Promise.all([
      store.dispatch(CONVERSATION__GET_CONVERSATIONS),
      store.dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES),
    ]);

    next();
  }
  // kinda gross but... else if customer is a property manager AND accessing anything but properties
  // but does NOT have a valid property_id stored OR the property_id from params
  // does NOT match the one stored...
  else if (
    isPropertyManager &&
    to.name !== 'properties' &&
    (!currentPropertyId || (propertyIdFromParams && currentPropertyId !== propertyIdFromParams))
  ) {
    _private.savedRouteTo = null;

    // first check if there is a property_id in the route params,
    // if so, set current propertyID and proceed
    if (propertyIdFromParams) {
      store.dispatch(PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID, {
        propertyID: _.toNumber(propertyIdFromParams),
      });

      next();
    } else {
      // else we don't know what property it is, so re-route
      // to properties and pass through the route and params ONLY
      // if we are not naving to the inbox
      const params = conversationIdFromParams
        ? {}
        : {
            toRoute: to.name,
            passThroughParams: to.params,
          };
      router.replace({
        name: 'properties',
        params,
      });
    }
  } else {
    // otherwise proceed on
    next();
  }
}

export function beforeEach(to, from, next) {
  // scroll to top on each route change
  window.scrollTo(0, 0);

  const { name, query } = to;

  // if an auth or access qp exists, save them and clear out the url
  if (query.auth || query.access) {
    if (query.auth) {
      store.dispatch(AUTH__UPDATE_TOKEN, query.auth);
      _.unset(to.query, 'auth');
    }

    if (query.access) {
      _private.access = query.access;
      _.unset(to.query, 'access');
    }

    const routeTo = {
      name: to.name,
      params: to.params,
      query: to.query,
    };

    // if we're routing to root "/", we don't want to save that routing
    // as the rest of the router will handle special cases accordingly
    if (routeTo.name !== 'guest') {
      _private.savedRouteTo = _.cloneDeep(routeTo);
    }

    return router.replace(routeTo);
  }

  to.meta.prevRouteName = from.name;

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // if this route requires auth
  if (requiresAuth) {
    // and they have an access code or saved token
    if (_private.access || store.getters[AUTH__TOKEN]) {
      // and they're already logged in
      if (store.getters[AUTH__IS_LOGGED_IN]) {
        // if they're onboarding and the navigation is leaving /policies for the first onboarding step,
        // with policies_accepted_at as truthy we know they just accepted the policies -- so we replace
        // the first item in history with /services and the route them to welcomeShare OR onboardingDistributeEarly if
        // orders.customers.distribute_early is truthy and it's before distribute_early_ends_at time

        // default to true in case the initial FEATURE_FLAGS__GET_FLAGS call fails
        const distributeEarlyFlagEnabled = _.get(
          store.getters[FEATURE_FLAGS__ALL_FLAGS],
          'orders.customers.distribute_early',
          true
        );

        // Ask users if they want to distribute early by default
        let canDistributeEarly = true;

        // If we're processing an order, check if the user can distribute early
        if (store.getters[AUTH__PROCESSING_ORDER]) {
          // Get the timestamp so that we know whether to show the onboardingDistributeEarly screen
          const { distribute_early_ends_at } = store.getters[AUTH__PROCESSING_ORDER];

          const now = dayjs.utc();
          let formattedDistributedEarlyEndsAt = null;
          if (!_.isNull(distribute_early_ends_at)) {
            formattedDistributedEarlyEndsAt = dayjs.utc(
              distribute_early_ends_at,
              'YYYY-MM-DD HH:mm:ss'
            );
          }

          canDistributeEarly =
            !_.isNull(formattedDistributedEarlyEndsAt) &&
            formattedDistributedEarlyEndsAt.isValid() &&
            now.isBefore(formattedDistributedEarlyEndsAt);
        }

        const lastRoute =
          distributeEarlyFlagEnabled && canDistributeEarly
            ? 'onboardingDistributeEarly'
            : 'onboardingPolicies';

        if (
          from.name === lastRoute &&
          to.path === _.get(store.getters[ONBOARDING__STEPS], '0.path') &&
          Boolean(_.get(store.getters[AUTH__PROCESSING_ORDER], 'policies_accepted_at'))
        ) {
          const nextRoute = store.getters[ONBOARDING__POST_ORDER_NEXT_SCREEN];

          router.replace({ name: 'services' }, () => {
            next({
              name: nextRoute,
            });
          });
        } else if (_private.savedRouteTo) {
          // otherwise if we saved the route we were trying to access earlier, go there
          const routeTo = _.cloneDeep(_private.savedRouteTo);
          _private.savedRouteTo = null;
          next(routeTo);
        } else {
          // otherwise just proceed on
          next();
        }
      } else {
        // otherwise, attempt to login and then proceed on success
        store.dispatch(_private.access ? AUTH__GET_TOKEN_FROM_ACCESS_CODE : AUTH__LOGIN, {
          access: _private.access,
          onSuccess() {
            onSuccess(to, null, next);
            _private.access = null;
            _private.savedRouteTo = null;
          },
        });
      }
    } else {
      // otherwise, route to /login (attempting to access a protected route without a token)
      next({
        name: 'login',
      });
    }
  } else if (_private.access || store.getters[AUTH__TOKEN]) {
    // else if route isn't protected, but they have an access code or authtoken
    // and they're logged in - re-route to /services
    if (store.getters[AUTH__IS_LOGGED_IN]) {
      next({
        name: 'services',
      });
    } else {
      // otherwise attempt to log them in and then route them to the default on success (/services)
      store.dispatch(_private.access ? AUTH__GET_TOKEN_FROM_ACCESS_CODE : AUTH__LOGIN, {
        access: _private.access,
      });
      _private.access = null;
    }
  } else {
    // finally, if the route is unprotected and they have no token, proceed
    if (name === 'guest') {
      next({ name: 'login' });
    } else {
      next();
    }
  }
}

let referrer = '';
export function afterEach(to, from) {
  if (!from.name) {
    referrer = _.get(document, 'referrer');
  }
  lsTrackingService.page({ path: to.path, referrer });
}

router.beforeEach((to, from, next) => beforeEach(to, from, next));
router.afterEach((to, from) => afterEach(to, from));

export default router;
